import { applyReceiptOptions, DEFAULT_TEMPLATES, devicesFor, fillTemplate, kickDrawer, printLabels, printTo, ResolvedDevice } from '@iws/pos-devices'
import { toast } from 'sonner'

import { useCallback } from 'react'

import { PAYMENT_METHODS } from '@/constants/payment'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { lineNet } from '@/services/PricingService'
import { currentCashier } from '@/lib/device'
import { deviceModels, readSetup } from '@/services/devices/setup'
import { ShiftSummary } from '@/db/repositories/shifts'
import { PosModel } from '@/types'
import { formatDateTime, formatMoney } from '@/utils'

import { ReceiptHeader, useReceiptHeader } from './receiptHeader'

const money = (n: number) => n.toFixed(2)
const paidInCash = (order: PosModel.Order) => order.payments.some((p) => p.method === 'cash' && p.amount > 0)
const opensDrawer = (d?: ResolvedDevice) => d?.model.protocol === 'escpos' || d?.model.protocol === 'star'

/** What the receipt template places (`DEFAULT_TEMPLATES.receipt`); values pre-formatted. */
export const receiptData = (order: PosModel.Order, header: ReceiptHeader, register: PosModel.Register) => {
  const t = order.totals
  const discount = t.lineDiscounts + t.orderDiscount
  return applyReceiptOptions({
    shop: {
      name: header.name,
      address: header.address,
      phone: header.phone,
      email: header.email,
      registration_no: header.registration_no,
      tax_id: header.tax_id,
      return_days: header.return_window_days,
    },
    options: { footer: header.footer || 'Thank you for shopping!' },
    order: {
      number: order.number,
      date: formatDateTime(order.created_at),
      counter: register.name,
      cashier: order.cashier,
      customer: order.customer ? { name: order.customer.name, phone: order.customer.phone } : null,
      lines: order.lines.map((l) => ({ name: l.product.name, qty: String(l.quantity), price: money(l.product.price), total: money(lineNet(l)), note: l.discount ? ' (disc)' : '' })),
      subtotal: money(t.subtotal),
      discount: discount > 0 ? money(discount) : '',
      tax: t.tax > 0 ? (register.tax_rate ? `${money(t.tax)} (${Math.round(register.tax_rate * 100)}%)` : money(t.tax)) : '',
      adjustment: t.adjustment > 0 ? money(t.adjustment) : '',
      total: `${register.currency} ${money(t.total)}`,
      payments: order.payments.map((p) => ({ method: PAYMENT_METHODS.find((m) => m.id === p.method)?.label ?? p.method, amount: money(p.amount) })),
      change: order.change > 0 ? money(order.change) : '',
      items: t.itemCount,
    },
  }, header.receipt)
}

export interface Slip {
  title: string
  /** Printed as a CODE128 barcode: `return_ref` or `receipt_no` (POS.md §18.8). */
  ref: string
  rows: [string, string][]
  created_at: string
}

/** The shift report's rows, in sections; the Shift page shows them and the Z / X report prints them. */
export const reportSections = (summary: ShiftSummary): { label: string; value: string; strong?: boolean }[][] => {
  const { shift } = summary
  const counted = shift.closed_at && shift.closing_balance !== undefined ? Number(shift.closing_balance) : null
  return [
    [
      ...(shift.shift_no ? [{ label: 'Shift', value: String(shift.shift_no) }] : []),
      { label: 'Opened', value: formatDateTime(String(shift.opened_at)) },
      ...(shift.closed_at ? [{ label: 'Closed', value: formatDateTime(String(shift.closed_at)) }] : []),
      { label: 'Sales', value: String(summary.orderCount) },
    ],
    [
      ...PAYMENT_METHODS.map((m) => ({ label: m.label, value: formatMoney(summary.sales[m.id]) })),
      { label: 'Refunds', value: formatMoney(-summary.refunds) },
      { label: 'Credit repaid', value: formatMoney(summary.repayments) },
    ],
    [
      { label: 'Opening float', value: formatMoney(Number(shift.opening_balance ?? 0)) },
      { label: 'Cash in', value: formatMoney(summary.cashIn) },
      { label: 'Cash out', value: formatMoney(-summary.cashOut) },
      { label: 'Expected in drawer', value: formatMoney(summary.expectedCash), strong: true },
      ...(counted !== null
        ? [
            { label: 'Counted', value: formatMoney(counted) },
            { label: 'Difference', value: formatMoney(counted - summary.expectedCash), strong: true },
          ]
        : []),
    ],
  ]
}

const fail = (error: unknown) => toast.error((error as Error).message, { id: 'printer' })

/**
 * Prints through this till's devices (POS.md §18.9): the first device on the `receipt` role, the drawer on
 * `cash_drawer` (or the receipt printer when it drives one). Read at print time, so a change applies at once.
 */
export const usePrinter = () => {
  const header = useReceiptHeader()
  const register = useRegisterContext()

  const receiptDevice = () => devicesFor(readSetup(), 'receipt', deviceModels())[0]

  const openDrawer = useCallback(async (printer?: ResolvedDevice) => {
    const drawer = devicesFor(readSetup(), 'cash_drawer', deviceModels())[0] ?? (opensDrawer(printer) ? printer : undefined)
    if (drawer) await kickDrawer(drawer)
  }, [])

  /** `sale`: the sale just completed, so the drawer may open; reprints never open it. */
  const printReceipt = useCallback(
    async (order: PosModel.Order, { sale = false } = {}) => {
      const printer = receiptDevice()
      if (!printer) return toast.error('No receipt printer is set up. Open Devices to add one.', { id: 'printer' })
      const setup = readSetup()
      const drawer = sale && !!setup.options?.open_drawer_on_cash && paidInCash(order)
      // The receipt printer opens its own drawer in the same job; any other drawer is kicked separately.
      const sameJob = drawer && opensDrawer(printer) && !(setup.roles.cash_drawer ?? []).length
      try {
        await printTo(printer, fillTemplate(DEFAULT_TEMPLATES.receipt, receiptData(order, header, register), { drawer: sameJob }), {
          title: order.number,
        })
        if (drawer && !sameJob) await openDrawer(printer)
      } catch (error) {
        fail(error)
      }
    },
    [header, register, openDrawer]
  )

  /** After a sale: print when auto-print is on, otherwise only open the drawer for cash. */
  const afterSale = useCallback(
    async (order: PosModel.Order) => {
      const setup = readSetup()
      if (setup.options?.auto_print) return printReceipt(order, { sale: true })
      if (setup.options?.open_drawer_on_cash && paidInCash(order)) await openDrawer(receiptDevice()).catch(fail)
    },
    [printReceipt, openDrawer]
  )

  const printSlip = useCallback(
    async (slip: Slip) => {
      const printer = receiptDevice()
      // Slips print on their own only on a real receipt printer; the browser dialog would interrupt every return.
      if (!printer || printer.device.transport === 'browser') return
      const data = {
        shop: { name: header.name, address: header.address },
        slip: { title: slip.title, ref: slip.ref, date: formatDateTime(slip.created_at), rows: slip.rows.map(([label, value]) => ({ label, value })) },
      }
      await printTo(printer, fillTemplate(DEFAULT_TEMPLATES.slip, data), { title: slip.ref }).catch(fail)
    },
    [header]
  )

  /** X report (open shift) or Z report (closed). `auto`: at shift close, printed only on a real receipt printer. */
  const printShiftReport = useCallback(
    async (summary: ShiftSummary, { auto = false } = {}) => {
      const printer = receiptDevice()
      if (!printer) return auto ? undefined : toast.error('No receipt printer is set up. Open Devices to add one.', { id: 'printer' })
      if (auto && printer.device.transport === 'browser') return
      const closed = !!summary.shift.closed_at
      const data = {
        shop: { name: header.name },
        shift: {
          title: closed ? 'Z REPORT' : 'X REPORT',
          counter: register.name,
          cashier: currentCashier()?.name ?? '',
          opened: formatDateTime(String(summary.shift.opened_at)),
          closed: closed ? formatDateTime(String(summary.shift.closed_at)) : 'open',
          rows: reportSections(summary).flat().map((r) => ({ label: r.label, value: r.value })),
        },
      }
      await printTo(printer, fillTemplate(DEFAULT_TEMPLATES.z_report, data), { title: data.shift.title }).catch(fail)
    },
    [header, register]
  )

  /**
   * A customer or staff card: on the label printer, else the A4 printer as a 2 × 5 card sheet. Customer cards made at
   * the till ("New card") print as they are saved; pre-printed cards are scanned in and need no print.
   */
  const printCard = useCallback(
    async (card: { kind: 'customer' | 'staff'; name: string; barcode: string }) => {
      const setup = readSetup()
      const label = devicesFor(setup, 'label', deviceModels())[0]
      const target = label ?? devicesFor(setup, 'document', deviceModels())[0]
      if (!target) return toast.error('No label or A4 printer is set up. Open Devices to add one.', { id: 'printer' })
      const template = card.kind === 'customer' ? DEFAULT_TEMPLATES.customer_card : DEFAULT_TEMPLATES.staff_card
      const data = { shop: { name: header.name }, [card.kind]: { name: card.name, barcode: card.barcode } }
      const sheet = !label || target.device.transport === 'browser' ? 'cards_2x5' : undefined
      await printLabels(target, template, [{ data, copies: 1 }], sheet).catch(fail)
    },
    [header]
  )

  return { printReceipt, afterSale, printSlip, printShiftReport, printCard }
}
